import {compact, noop, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ReactFlow,
  applyNodeChanges,
  Connection,
  NodeChange,
  NodePositionChange,
  OnConnectStartParams,
  PanOnScrollMode,
  ReactFlowProps,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import * as uuid from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import * as contextmenu from 'components/ContextMenu';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  ProcessClientOrigin,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartActionFunction,
  FlowchartDragDropKey,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import computeDefaultPositionsForFlowchart, {
  EdgeClientIdToLabelDictionary,
} from 'processes/graph-builder/flowchart-viz/util/computeDefaultPositionsForFlowchart';
import {PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartInteractiveEdgeRenderer';
import FlowchartCacheDataProvider from 'processes/graph-builder-processes/flowchart-viz/util/FlowchartCacheDataProvider';
import {
  isEndNode,
  isOutputNode,
  isRootNode,
  isRouterNode,
  isTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForInteractiveNode';
import useFlowchartDataCache, {
  DeleteDataForEdge,
} from 'processes/graph-builder-processes/flowchart-viz/util/useFlowchartDataCache';
import {
  END_NODE_INFO,
  getNodeInstanceData,
  ROUTER_NODE_INFO,
} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import {
  ProcessFlowchartConfigForGraphBuilder,
  ProcessFlowchartDesignSpace,
  ProcessGraphBuilderNodeTemplate,
  ProcessTaskSchemaForGraphBuilderValidation,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {getUnconfiguredRouterConfig} from 'processes/modals/router-config/RouterConfigConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import {assertNever, ElementDragEvent} from 'util/Types';

import {FlowchartValidationErrorType} from '../validation/ProcessFlowchartForValidationTypes';
import configureRouterNodeProcessFlowchartActionFunction from './action-functions/configureRouterProcessFlowchartActionFunction';
import deleteEdgeProcessFlowchartActionFunction, {
  deleteEdgeFromFlowchart,
} from './action-functions/deleteEdgeProcessFlowchartActionFunction';
import deleteNodeProcessFlowchartActionFunction, {
  deleteNodeFromFlowchart,
  deleteRouterNodeFromFlowchart,
} from './action-functions/deleteNodeProcessFlowchartActionFunction';
import ProcessFlowchartContext from './ProcessFlowchartContext';
import ProcessFlowchartEditorControls, {
  ProcessFlowchartEditorAdjustmentType,
} from './ProcessFlowchartEditorControls';
import {PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER} from './ProcessFlowchartInteractiveNodeRenderer';
import ProcessFlowchartSidebar from './ProcessFlowchartSidebar';
import {
  ActionFunctionsPerInteractiveNodeType,
  DEFAULT_FIT_VIEW_OPTIONS,
  MAX_FLOWCHART_ZOOM,
  MIN_FLOWCHART_ZOOM,
  PROCESS_FLOWCHART_EDITOR_PADDING,
  ProcessFlowchartHandleClassName,
  ProcessFlowchartInteractiveEdge,
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartNodeHeight,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartRenderData,
  ProcessInteractiveFlowchart,
} from './ProcessFlowchartVizConstants';
import convertInteractiveProcessFlowchartForReactFlow from './util/convertInteractiveProcessFlowchartForReactFlow';
import {getClientIdForProcessFlowchartEdge} from './util/ConvertProcessFlowchartForReactFlowUtil';
import findUpstreamTaskNodes from './util/findUpstreamTaskNodes';
import {flowchartHasChainedRouters} from './util/flowchartHasChainedRouters';
import {getNewNodeName} from './util/getNewNodeName';
import * as StubbableFunctions from './util/stubbableFunctions';

export interface ProcessFlowchartEditorProps {
  flowchart: ProcessInteractiveFlowchart;
  designSpace: ProcessFlowchartDesignSpace;
  locked?: boolean;
  hideSidebar?: boolean;
  onFlowchartChange: (
    flowchart: ProcessInteractiveFlowchart,
    flowchartErrors: Array<FlowchartValidationErrorType>
  ) => void;
  reactFlowEmbeddedElement?: JSX.Element | null;
  editorEventGroupId: string;
  actionMethod: ActionMethod;
  didInitialFlowchartHaveChainedRouters: boolean;
  modalType?: string;
}

export enum MODIFICATION_ACTION {
  DRAGGED_NODE_FROM_SIDEBAR = 'DRAGGED_NODE_FROM_SIDEBAR',
  CREATED_NODE_FROM_EDGE_DROP = 'CREATED_NODE_FROM_EDGE_DROP',
  CONNECTED_EDGE = 'CONNECTED_EDGE',
  CREATED_EDGE_FROM_EDGE_DROP = 'CREATED_EDGE_FROM_EDGE_DROP',
  DELETED_NODE = 'DELETED_NODE',
  DELETED_EDGE = 'DELETED_EDGE',
}

const DROP_X_OFFSET = -35;
const DROP_Y_OFFSET = -25;

function getReactFlowInteractionProps(locked: boolean): ReactFlowProps {
  return {
    nodesConnectable: !locked,
    nodesDraggable: !locked,
    zoomOnScroll: false,
    panOnScroll: true,
    panOnScrollMode: PanOnScrollMode.Vertical,
    zoomOnDoubleClick: false,
    panOnDrag: true,
    snapToGrid: true,
  };
}

function buildSchemaIdToCounter(
  schemas: Array<{schemaId: string}>,
  nodeSchemasOrDragDropKeys: Array<string> = []
): {[schemaId: string]: number} {
  const counter = objectFromEntries(schemas.map((schema) => [schema.schemaId, 0]));
  counter[ROUTER_NODE_INFO.nodeDefinition.dropKey] = 0;
  counter[END_NODE_INFO.nodeDefinition.dropKey] = 0;
  for (const schemaId of nodeSchemasOrDragDropKeys) {
    if (counter[schemaId] != null) {
      counter[schemaId]++;
    } else {
      counter[schemaId] = 1;
    }
  }

  return counter;
}

function buildActionFunctionsForNodes({
  flowchart,
  onChangeFlowchart,
  flowchartSchema,
  flowchartConfig,
  editorEventGroupId,
  analyticsMetadata,
  didInitialFlowchartHaveChainedRouters,
  doesCurrentFlowchartHaveChainedRouters,
  modalType,
}: {
  flowchart: ProcessInteractiveFlowchart;
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void;
  flowchartSchema: ProcessTaskSchemaForGraphBuilderValidation;
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder;
  editorEventGroupId: string;
  analyticsMetadata: FlowchartAnalyticsMetadata;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  modalType?: string;
}): ActionFunctionsPerInteractiveNodeType {
  return {
    TASK_NODE_DETAILS: [
      deleteNodeProcessFlowchartActionFunction(
        flowchart,
        deleteNodeFromFlowchart,
        onChangeFlowchart,
        flowchartSchema.id,
        editorEventGroupId,
        analyticsMetadata,
        modalType
      ),
    ],
    ROUTER_NODE_DETAILS: [
      configureRouterNodeProcessFlowchartActionFunction({
        flowchart,
        onChangeFlowchart,
        flowchartSchema,
        flowchartConfig,
        editorEventGroupId,
        flowchartAnalyticsMetatdata: analyticsMetadata,
        actionOrigin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
        didInitialFlowchartHaveChainedRouters,
        doesCurrentFlowchartHaveChainedRouters,
      }),
      deleteNodeProcessFlowchartActionFunction(
        flowchart,
        deleteRouterNodeFromFlowchart,
        onChangeFlowchart,
        flowchartSchema.id,
        editorEventGroupId,
        analyticsMetadata,
        modalType
      ),
    ],
    ROOT_NODE_DETAILS: [],
    OUTPUT_NODE_DETAILS: [],
    END_NODE_DETAILS: [
      deleteNodeProcessFlowchartActionFunction(
        flowchart,
        deleteNodeFromFlowchart,
        onChangeFlowchart,
        flowchartSchema.id,
        editorEventGroupId,
        analyticsMetadata,
        modalType
      ),
    ],
  };
}

function buildActionFunctionsForEdge(
  flowchart: ProcessInteractiveFlowchart,
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void,
  deleteDataForEdge: DeleteDataForEdge,
  flowchartSchemaId: string,
  flowchartConfig: ProcessFlowchartConfigForGraphBuilder,
  editorEventGroupId: string,
  analyticsMetadata: FlowchartAnalyticsMetadata,
  modalType?: string
): Array<FlowchartActionFunction<ProcessFlowchartInteractiveEdge, ProcessInteractiveFlowchart>> {
  const cacheAwareDeleteEdge = (
    edgeClientId: string,
    flowchart: ProcessInteractiveFlowchart
  ): ProcessInteractiveFlowchart => {
    deleteDataForEdge(edgeClientId);
    return deleteEdgeFromFlowchart(edgeClientId, flowchart);
  };
  return [
    deleteEdgeProcessFlowchartActionFunction(
      flowchart,
      cacheAwareDeleteEdge,
      onChangeFlowchart,
      flowchartSchemaId,
      editorEventGroupId,
      analyticsMetadata,
      modalType
    ),
  ];
}

export interface FlowchartAnalyticsMetadata {
  processClientOrigin: ProcessClientOrigin;
  actionMethod: ActionMethod;
}

function buildConvertedFlowchart(
  flowchart: ProcessInteractiveFlowchart,
  locked: boolean,
  designSpace: ProcessFlowchartDesignSpace,
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void,
  deleteDataForEdge: DeleteDataForEdge,
  editorEventGroupId: string,
  actionMethod: ActionMethod,
  didInitialFlowchartHaveChainedRouters: boolean,
  modalType?: string
): ProcessFlowchartRenderData {
  return convertInteractiveProcessFlowchartForReactFlow(
    flowchart,
    locked,
    designSpace,
    buildActionFunctionsForNodes({
      flowchart,
      onChangeFlowchart,
      flowchartSchema: designSpace.flowchartSchema,
      flowchartConfig: designSpace.flowchartConfig,
      editorEventGroupId,
      analyticsMetadata: {actionMethod, processClientOrigin: designSpace.flowchartSchema.clientOrigin},
      didInitialFlowchartHaveChainedRouters,
      doesCurrentFlowchartHaveChainedRouters: flowchartHasChainedRouters(flowchart),
      modalType,
    }),
    buildActionFunctionsForEdge(
      flowchart,
      onChangeFlowchart,
      deleteDataForEdge,
      designSpace.flowchartSchema.id,
      designSpace.flowchartConfig,
      editorEventGroupId,
      {actionMethod, processClientOrigin: designSpace.flowchartSchema.clientOrigin},
      modalType
    ),
    didInitialFlowchartHaveChainedRouters
  );
}

function setDefaultNodeNames(
  flowchart: ProcessInteractiveFlowchart,
  taskSchemas: Array<{schemaId: string; name: string}>,
  flowchartSchema: {id: string}
): ProcessInteractiveFlowchart {
  const schemaIdCounter = buildSchemaIdToCounter(taskSchemas);
  const schemaNameById: {[id: string]: string} = {};
  for (const schema of taskSchemas) {
    schemaNameById[schema.schemaId] = schema.name;
  }

  return produce(flowchart, (draftFlowchart) => {
    for (const node of draftFlowchart.nodes) {
      if (isRouterNode(node) || isEndNode(node)) {
        continue;
      }
      const nodeCount = schemaIdCounter[node.taskSchemaId];
      const schemaName = schemaNameById[node.taskSchemaId];

      if (isOutputNode(node)) {
        node.name = 'Outputs';
      } else if (schemaName != null && node.taskSchemaId !== flowchartSchema.id) {
        node.name = `${schemaName} ${nodeCount + 1}`;
      }
      schemaIdCounter[node.taskSchemaId]++;
    }
  });
}

function buildFlowchartWithDefaultNames(
  flowchart: ProcessInteractiveFlowchart,
  taskSchemas: Array<{schemaId: string; name: string}>,
  flowchartSchema: {id: string}
): ProcessInteractiveFlowchart {
  const rootNode = flowchart.nodes.find((node) => isRootNode(node));
  assert(rootNode != null, 'Root node must exist');

  return setDefaultNodeNames(flowchart, taskSchemas, flowchartSchema);
}

function EmptyProcessFlowchartEditor(props: {locked?: boolean}): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  return (
    <ReactFlowWrapper width={ProcessFlowchartNodeWidth.WIDE}>
      <ReactFlow
        {...defaultReactFlowProps}
        {...getReactFlowInteractionProps(props.locked ?? false)}
        nodes={[]}
        edges={[]}
        onNodesChange={noop}
        nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER}
        edgeTypes={PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER}
      />
    </ReactFlowWrapper>
  );
}

const enum FlowchartActionType {
  HARD_SET_FLOWCHART,
}

interface SetFlowchartAction {
  type: FlowchartActionType.HARD_SET_FLOWCHART;
  newFlowchart: ProcessInteractiveFlowchart;
}

function flowchartReducer(
  flowchart: ProcessInteractiveFlowchart,
  action: SetFlowchartAction
): ProcessInteractiveFlowchart {
  switch (action.type) {
    case FlowchartActionType.HARD_SET_FLOWCHART:
      return action.newFlowchart;

    default:
      return flowchart;
  }
}

function makeNewEdge(
  fromNodeClientId: string,
  toNodeClientId: string,
  clientId: string
): ProcessFlowchartInteractiveEdge {
  return {
    clientId,
    fromNodeClientId,
    toNodeClientId,
  };
}

// exported for test only
export function getPotentialTargetNodes(
  sourceNode: ProcessFlowchartInteractiveNode,
  flowchart: ProcessInteractiveFlowchart,
  designSpace: ProcessFlowchartDesignSpace
): Array<ProcessGraphBuilderNodeTemplate> {
  if (isTaskNode(sourceNode) || isRootNode(sourceNode)) {
    return compact(
      designSpace.availableEdges
        .filter(
          (edge) =>
            (isRootNode(sourceNode) || isTaskNode(sourceNode)) && edge.fromNodeId === sourceNode.taskSchemaId
        )
        .map((edge) => designSpace.availableNodes.find((node) => node.schemaId === edge.toNodeId))
    ).filter((node) => node.schemaId !== designSpace.flowchartSchema.id);
  } else if (isRouterNode(sourceNode)) {
    const upstreamNodes = findUpstreamTaskNodes(sourceNode, flowchart);
    if (upstreamNodes.length !== 1) {
      return [];
    }
    return getPotentialTargetNodes(upstreamNodes[0], flowchart, designSpace);
  } else if (isOutputNode(sourceNode) || isEndNode(sourceNode)) {
    return [];
  } else {
    assert(false, `Invalid node type ${assertNever(sourceNode)}`);
  }
}

// The user has initiated an edge. They released the mouse button in empty space. We offer them
// a menu of possible new nodes to create, then create the node and make an edge for them.
function openEdgeConnectionContextMenu({
  sourceNode,
  flowchart,
  designSpace,
  event,
  onSelectTargetNode,
}: {
  sourceNode: ProcessFlowchartInteractiveNode;
  flowchart: ProcessInteractiveFlowchart;
  designSpace: ProcessFlowchartDesignSpace;
  event: MouseEvent | TouchEvent;
  onSelectTargetNode: (dropKey: string) => void;
}): void {
  const potentialTargetNodes = getPotentialTargetNodes(sourceNode, flowchart, designSpace);
  if (potentialTargetNodes.length === 1) {
    onSelectTargetNode(potentialTargetNodes[0].schemaId);
  } else if (potentialTargetNodes.length > 0) {
    const menuOptions: Array<contextmenu.ContextMenuOption> = potentialTargetNodes.map((node) => ({
      name: node.name,
      click: () => {
        onSelectTargetNode(node.schemaId);
      },
    }));
    const disableRouterOption = isRouterNode(sourceNode);
    menuOptions.unshift(
      {
        name: 'Router',
        click: () => {
          onSelectTargetNode(ROUTER_NODE_INFO.nodeDefinition.dropKey);
        },
        disabled: disableRouterOption,
        tooltip: disableRouterOption
          ? "Benchling doesn't support chained routers. Use multiple conditions or condition groups within a single router instead."
          : null,
      },
      '-'
    );

    contextmenu.view.open(event, menuOptions, true);
  }
}

// This selectively enables or disables pointer events on handles. This allows us to suppress
// the initiation of an edge by clicking on a target handle. Edges must be initiated from a source handle.
function enableHandlePointerEvents(
  cls: keyof typeof ProcessFlowchartHandleClassName,
  acceptEvents: boolean
): void {
  const pointerEvents = acceptEvents ? 'all' : 'none';
  document.querySelectorAll<HTMLDivElement>(`.${ProcessFlowchartHandleClassName[cls]}`).forEach((handle) => {
    handle.style.pointerEvents = pointerEvents;
  });
}

function isTouchEvent(e: TouchEvent | MouseEvent): e is TouchEvent {
  return e && 'touches' in e;
}

function isPositionChange(change: NodeChange): change is NodePositionChange {
  return change.type === 'position';
}

function extractGraphBuilderErrorTypes(
  convertedFlowchart: ProcessFlowchartRenderData
): Array<FlowchartValidationErrorType> {
  const errorTypes: Array<FlowchartValidationErrorType> = [];
  for (const node of convertedFlowchart.nodes) {
    errorTypes.push(...(node.data.errorTypes || []));
  }
  for (const edge of convertedFlowchart.edges) {
    errorTypes.push(...(edge.data.errorTypes || []));
  }
  return errorTypes;
}

function getEdgeLabelDictionary(
  convertedFlowchart: ProcessFlowchartRenderData
): EdgeClientIdToLabelDictionary {
  const edgesWithLabels = convertedFlowchart.edges.filter((e) => e.label);
  const dictionary: EdgeClientIdToLabelDictionary = {};
  edgesWithLabels.forEach((reactFlowEdge) => {
    dictionary[reactFlowEdge.data.clientId] =
      typeof reactFlowEdge.label === 'string' ? reactFlowEdge.label : null;
  });

  return dictionary;
}

export const stubbableFunctions = {
  makeClientIdSuffix: (): string => uuid.v4(),
};

function InnerProcessFlowchartEditor(
  props: ProcessFlowchartEditorProps & {reactFlowContainerWidth?: number}
): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const reactFlowInstance = useReactFlow();
  const [shouldFitView, setShouldFitView] = useState(true);
  const [hideTooltips, setHideTooltips] = useState(false);
  const nodeThatInitiatedEdgeDrag = useRef<string | null>(null);
  const {deleteDataForEdge, emptyCache} = useFlowchartDataCache();
  const [flowchart, reduceFlowchart] = useReducer(
    flowchartReducer,
    buildFlowchartWithDefaultNames(
      props.flowchart,
      props.designSpace.availableNodes,
      props.designSpace.flowchartSchema
    )
  );
  const flowchartSchemaId = props.designSpace.flowchartSchema.id;

  // It's not a problem to use this HARD_SET reducer. Use it if you prefer to keep the bulk of the logic
  // local in this component, rather than in the reducer above. However, a reducer has better protection
  // against collisions (multiple simultaneous updates), so in general you should prefer to extend the reducer.
  const setFlowchart = (newFlowchart: ProcessInteractiveFlowchart): void => {
    reduceFlowchart({type: FlowchartActionType.HARD_SET_FLOWCHART, newFlowchart});
  };

  const doesEdgeExist = useCallback(
    (sourceNodeId: string, targetNodeId: string): boolean =>
      flowchart.edges.find(
        ({fromNodeClientId, toNodeClientId}) =>
          fromNodeClientId === sourceNodeId && toNodeClientId === targetNodeId
      ) != null,
    [flowchart.edges]
  );

  const convertedFlowchart = useMemo(
    () =>
      buildConvertedFlowchart(
        flowchart,
        props.locked ?? false,
        props.designSpace,
        setFlowchart,
        deleteDataForEdge,
        props.editorEventGroupId,
        props.actionMethod,
        props.didInitialFlowchartHaveChainedRouters,
        props.modalType
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flowchart]
  );

  const onNodesChange = (changes: Array<NodeChange>): void => {
    const updatedNodes = applyNodeChanges(changes, convertedFlowchart.nodes);
    const movedNodeClientIds = compact(
      changes.map((change) => {
        if (isPositionChange(change) && change.dragging) {
          return change.id;
        }
        return null;
      })
    );

    // Remove any cached edge layout information for all edges that this node touches.
    // We're removing both the edge layout data from autolayout (a raw array of xy points)
    // as well as the computed svg path info (a string)
    let newEdges = flowchart.edges;
    if (movedNodeClientIds.length !== 0) {
      newEdges = flowchart.edges.map((edge) => {
        if (
          movedNodeClientIds.includes(edge.fromNodeClientId) ||
          movedNodeClientIds.includes(edge.toNodeClientId)
        ) {
          deleteDataForEdge(edge.clientId);
          return {...edge, layout: undefined};
        }
        return edge;
      });
    }

    // We inject the flowchart with updated node positions when nodes are dragged in the canvas
    // The flowchart is used to generate the node positions that are passed back to ReactFlow for rendering
    setFlowchart({
      ...flowchart,
      // The node.data property contains an actionFunction array which has a reference to the flowchart itself.
      // This makes the flowchart recursive which can cause infinite loops when processing and evaluating
      // the flowchart object.
      nodes: updatedNodes.map(({data, position}) => {
        const {actionFunctions, ...nodeData} = data;
        return {
          ...nodeData,
          layout: {position},
        };
      }),
      edges: newEdges,
    });
  };

  const makeNewNode = useCallback(
    (dropX: number, dropY: number, dropKey: string): ProcessFlowchartInteractiveNode | null => {
      const zoomScale = reactFlowInstance.getZoom();
      const position = reactFlowInstance.project({
        x: dropX + DROP_X_OFFSET * zoomScale,
        y: dropY + DROP_Y_OFFSET * zoomScale,
      });
      let newNode: ProcessFlowchartInteractiveNode | null = null;
      const existingNodeDetailTypesAndNames = flowchart.nodes.map((node) => ({
        nodeDetailsType: node.type,
        nodeName: node.name,
      }));
      const nodeName = getNewNodeName(
        existingNodeDetailTypesAndNames,
        dropKey,
        schemaIdToCounter.current[dropKey],
        props.designSpace.availableNodes
      );

      if (dropKey === ROUTER_NODE_INFO.nodeDefinition.dropKey) {
        const clientId = `${
          ROUTER_NODE_INFO.nodeDefinition.dropKey
        }/${stubbableFunctions.makeClientIdSuffix()}`;
        newNode = {
          clientId,
          type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
          name: nodeName,
          instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS, nodeName),
          locked: false,
          layout: {
            position,
          },
          routerConfig: getUnconfiguredRouterConfig(nodeName),
        };
      } else if (dropKey === END_NODE_INFO.nodeDefinition.dropKey) {
        const clientId = `${END_NODE_INFO.nodeDefinition.dropKey}/${stubbableFunctions.makeClientIdSuffix()}`;
        newNode = {
          clientId,
          type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
          name: nodeName,
          instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.END_NODE_DETAILS, nodeName),
          locked: false,
          layout: {
            position,
          },
        };
      } else {
        const schemaId = dropKey;
        newNode = {
          clientId: `${schemaId}/${stubbableFunctions.makeClientIdSuffix()}`,
          type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
          taskSchemaId: schemaId,
          name: nodeName,
          instanceData: getNodeInstanceData(ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS, nodeName),
          locked: false,
          layout: {position},
        };
      }
      schemaIdToCounter.current[dropKey]++;
      return newNode;
    },
    [props.designSpace.availableNodes, reactFlowInstance, flowchart.nodes]
  );

  const handleConnectStart = useCallback(
    (event: ReactMouseEvent | ReactTouchEvent, {nodeId}: OnConnectStartParams): void => {
      nodeThatInitiatedEdgeDrag.current = nodeId;
      setDragStatus(true);
      // Target handles should temporarily allow pointer events, for completing a new edge. For the
      // same reasons, we disable pointer events on source handles, which are not drop zones
      enableHandlePointerEvents('TARGET', true);
      enableHandlePointerEvents('SOURCE', false);
    },
    []
  );

  // Invoked when the user clicks to end an edge-drag operation. This is only called if the operation
  // ends somewhere other than a normal input handle.
  const handleConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLDivElement | null;
      // The ReactFlow team is aware that we shouldn't really inspect their internal classes.
      const targetIsReactFlowPane = target?.classList.contains('react-flow__pane');
      const sourceNode = flowchart.nodes.find((n) => n.clientId === nodeThatInitiatedEdgeDrag.current);

      setDragStatus(false);

      if (!targetIsReactFlowPane || nodeThatInitiatedEdgeDrag.current == null || sourceNode == null) {
        return;
      }

      const boundingRect = target!.getBoundingClientRect();
      let clientX;
      let clientY;

      if (isTouchEvent(event)) {
        const touch = event.touches[0];
        if (!touch) {
          return;
        }
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      const eventX = clientX - boundingRect.left;
      const eventY = clientY - boundingRect.top;

      openEdgeConnectionContextMenu({
        sourceNode,
        flowchart,
        designSpace: props.designSpace,
        event,
        onSelectTargetNode: (dropKey) => {
          const newNode = makeNewNode(eventX, eventY, dropKey);
          if (newNode != null) {
            const sourceNodeId = sourceNode.clientId;
            const newNodeId = newNode.clientId;
            const edgeClientId = getClientIdForProcessFlowchartEdge(sourceNodeId, newNodeId);
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
              process_client_origin: props.designSpace.flowchartSchema.clientOrigin,
              action_type: MODIFICATION_ACTION.CREATED_NODE_FROM_EDGE_DROP,
              modified_item: flowchartSchemaId,
              nodes_modified: [newNodeId],
              edges_modified: [],
              event_group_id: props.editorEventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
              action_method: props.actionMethod,
              modal_type: props.modalType,
            });
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
              process_client_origin: props.designSpace.flowchartSchema.clientOrigin,
              action_type: MODIFICATION_ACTION.CREATED_EDGE_FROM_EDGE_DROP,
              modified_item: flowchartSchemaId,
              nodes_modified: [sourceNodeId, newNodeId],
              edges_modified: [edgeClientId],
              event_group_id: props.editorEventGroupId,
              action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
              action_method: props.actionMethod,
              modal_type: props.modalType,
            });
            const newEdge = makeNewEdge(sourceNode.clientId, newNode.clientId, edgeClientId);
            setFlowchart({
              ...flowchart,
              nodes: [...flowchart.nodes, newNode],
              edges: [...flowchart.edges, newEdge],
            });
          }
        },
      });
    },
    [
      flowchart,
      makeNewNode,
      props.designSpace,
      flowchartSchemaId,
      props.editorEventGroupId,
      props.actionMethod,
      props.modalType,
    ]
  );

  const handleConnect = useCallback(
    ({source, target}: Connection): void => {
      const edgeDoesNotAlreadyExistOrFormSelfCycle =
        source != null && target != null && !doesEdgeExist(source, target) && source !== target;
      if (edgeDoesNotAlreadyExistOrFormSelfCycle) {
        const edgeClientId = getClientIdForProcessFlowchartEdge(source, target);
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
          process_client_origin: props.designSpace.flowchartSchema.clientOrigin,
          action_type: MODIFICATION_ACTION.CONNECTED_EDGE,
          modified_item: flowchartSchemaId,
          nodes_modified: [source, target],
          edges_modified: [edgeClientId],
          event_group_id: props.editorEventGroupId,
          action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
          action_method: props.actionMethod,
          modal_type: props.modalType,
        });
        setFlowchart({
          ...flowchart,
          edges: [...flowchart.edges, makeNewEdge(source, target, edgeClientId)],
        });
      }
    },
    [
      doesEdgeExist,
      flowchart,
      flowchartSchemaId,
      props.actionMethod,
      props.editorEventGroupId,
      props.modalType,
      props.designSpace.flowchartSchema.clientOrigin,
    ]
  );

  const handleNodeDrag = useCallback((event: ElementDragEvent): void => {
    event.preventDefault();
    setDragStatus(true);
  }, []);

  const handleNodeDragStop = useCallback((event: ElementDragEvent) => {
    event.preventDefault();
    setDragStatus(false);
  }, []);

  const handleDragOver = useCallback((event: ElementDragEvent): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event: ElementDragEvent): void => {
      event.preventDefault();
      const boundingRect = event.currentTarget.getBoundingClientRect();
      const nodeKey = event.dataTransfer.getData(FlowchartDragDropKey);
      if (nodeKey) {
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
          process_client_origin: props.designSpace.flowchartSchema.clientOrigin,
          action_type: MODIFICATION_ACTION.DRAGGED_NODE_FROM_SIDEBAR,
          modified_item: flowchartSchemaId,
          nodes_modified: [nodeKey],
          edges_modified: [],
          event_group_id: props.editorEventGroupId,
          action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
          action_method: props.actionMethod,
          modal_type: props.modalType,
        });
        const newNode = makeNewNode(
          event.clientX - boundingRect.left,
          event.clientY - boundingRect.top,
          nodeKey
        );
        if (newNode != null) {
          setFlowchart({...flowchart, nodes: [...flowchart.nodes, newNode]});
        }
      }
    },
    [
      flowchart,
      flowchartSchemaId,
      makeNewNode,
      props.actionMethod,
      props.editorEventGroupId,
      props.modalType,
      props.designSpace.flowchartSchema.clientOrigin,
    ]
  );

  const handleMove = useCallback((e: MouseEvent) => {
    // If this a zoom event from the toolbar, the event is null
    // (Otherwise it's a mouse event to pan or a wheel event to zoom)
    // Reactflow won't call onMoveEnd when zoom is invoked from the toolbar,
    // so we can't hide the tooltips because they will get stuck in that state
    // until something else enables them
    if (e != null) {
      setHideTooltips(true);
    }
  }, []);

  const handleMoveEnd = useCallback(() => {
    setHideTooltips(false);
  }, []);

  const handleSelectionDragStart = useCallback(() => {
    setHideTooltips(true);
  }, []);

  const handleSelectionDragStop = useCallback(() => {
    setHideTooltips(false);
  }, []);

  const schemaIdToCounter = useRef<{[schemaId: string]: number}>(
    buildSchemaIdToCounter(
      props.designSpace.availableNodes,
      compact(
        flowchart.nodes.map((node) => {
          if (isRouterNode(node)) {
            return ROUTER_NODE_INFO.nodeDefinition.dropKey;
          } else if (isEndNode(node)) {
            return END_NODE_INFO.nodeDefinition.dropKey;
          } else {
            return isTaskNode(node) || isRootNode(node) || isOutputNode(node) ? node.taskSchemaId : null;
          }
        })
      )
    )
  );

  // We need to manually call `fitView` for the initial graph. We wait for the parent's
  // width to be known before calling `fitView`.
  useEffect(() => {
    if (shouldFitView && props.reactFlowContainerWidth != null) {
      const edgeClientIdToLabelDictionary = getEdgeLabelDictionary(convertedFlowchart);
      setFlowchart(
        computeDefaultPositionsForFlowchart(flowchart, edgeClientIdToLabelDictionary, {
          defaultNodeWidth: ProcessFlowchartNodeWidth.DEFAULT,
          defaultNodeHeight: ProcessFlowchartNodeHeight.DEFAULT,
          flowchartEditorPadding: PROCESS_FLOWCHART_EDITOR_PADDING,
        })
      );

      // We defer the call to `fitView` until the next render cycle, after the newly
      // formatted flowchart has been prepared.
      const timeout = setTimeout(() => {
        reactFlowInstance.fitView(DEFAULT_FIT_VIEW_OPTIONS);
      });
      setHideTooltips(false);
      setShouldFitView(false);

      return () => {
        clearTimeout(timeout);
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.reactFlowContainerWidth]);

  useEffect(() => {
    const flowchartErrors = extractGraphBuilderErrorTypes(convertedFlowchart);
    if (!hideTooltips) {
      props.onFlowchartChange(flowchart, flowchartErrors);
    }

    // Ensure than any time the flowchart changes, we set any new target handles to reject pointer events,
    // and source handles to accept them
    enableHandlePointerEvents('TARGET', false);
    enableHandlePointerEvents('SOURCE', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchart, convertedFlowchart]);

  const setDragStatus = (isAnyDragInProcess: boolean): void => {
    setHideTooltips(isAnyDragInProcess);
  };

  if (props.reactFlowContainerWidth == null) {
    // We need to wait until the parent's width is known before we render the actual flowchart so ReactFlow
    // can fit the flowchart to the canvas. So here we just render an empty canvas with a hardcoded width.
    return <EmptyProcessFlowchartEditor locked={props.locked} />;
  }

  return (
    <ReactFlowWrapper
      width={props.reactFlowContainerWidth}
      containerWidthForControls={props.reactFlowContainerWidth}
    >
      {(reactFlowControlsXPosition) => (
        // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
        <ProcessFlowchartContext.Provider value={{hideTooltips, flowchartClientId: props.flowchart.clientId}}>
          <div
            className={css(styles.flowchartInnerWrapper)}
            {...getTestProps({component: 'ProcessFlowchartEditor', element: 'root'})}
          >
            <div
              className={css(props.hideSidebar ? styles.flowchartCanvasNoSidebar : styles.flowchartCanvas)}
            >
              <ReactFlow
                {...defaultReactFlowProps}
                {...getReactFlowInteractionProps(props.locked ?? false)}
                {...getTestProps({component: 'ReactFlow', element: 'root'})}
                nodes={convertedFlowchart.nodes}
                edges={convertedFlowchart.edges}
                onNodesChange={onNodesChange}
                nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER}
                edgeTypes={PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER}
                onConnectStart={handleConnectStart}
                onConnect={handleConnect}
                onConnectEnd={handleConnectEnd}
                onNodeDrag={handleNodeDrag}
                onNodeDragStop={handleNodeDragStop}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onMove={handleMove}
                onMoveEnd={handleMoveEnd}
                onSelectionDragStart={handleSelectionDragStart}
                onSelectionDragStop={handleSelectionDragStop}
                fitView={true}
                fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
                minZoom={MIN_FLOWCHART_ZOOM}
                maxZoom={MAX_FLOWCHART_ZOOM}
              >
                {reactFlowControlsXPosition != null && (
                  <ProcessFlowchartEditorControls
                    reactFlowControlsXPosition={reactFlowControlsXPosition}
                    editorEventGroupId={props.editorEventGroupId}
                    actionMethod={props.actionMethod}
                    modalType={props.modalType}
                  />
                )}
                {props.reactFlowEmbeddedElement}
              </ReactFlow>
            </div>
            {!props.locked && (
              <Button
                className={css(styles.autoLayoutButton)}
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                text="Auto layout"
                icon="magic-wand"
                onClick={() => {
                  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ADJUSTED_CANVAS_WORKSPACE, {
                    process_client_origin: props.designSpace.flowchartSchema.clientOrigin,
                    adjustment_type: ProcessFlowchartEditorAdjustmentType.AUTOLAYOUT,
                    event_group_id: props.editorEventGroupId,
                    action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
                    action_method: props.actionMethod,
                    modal_type: props.modalType,
                  });
                  emptyCache(); // clear cached layout data since autolayout is about to run
                  const edgeClientIdToLabelDictionary = getEdgeLabelDictionary(convertedFlowchart);
                  setFlowchart(
                    computeDefaultPositionsForFlowchart(flowchart, edgeClientIdToLabelDictionary, {
                      defaultNodeWidth: ProcessFlowchartNodeWidth.DEFAULT,
                      defaultNodeHeight: ProcessFlowchartNodeHeight.DEFAULT,
                      flowchartEditorPadding: PROCESS_FLOWCHART_EDITOR_PADDING,
                    })
                  );
                  // We have to defer the call to `fitView` until the next render cycle, after the newly
                  // formatted flowchart has been prepared.
                  setTimeout(() => {
                    reactFlowInstance.fitView(DEFAULT_FIT_VIEW_OPTIONS);
                  });
                  setHideTooltips(false);
                }}
                dataTestAttrs={{
                  component: 'ProcessFlowchartEditor',
                  element: 'autoLayoutButton',
                }}
              />
            )}
            {!props.hideSidebar && (
              <div>
                <ProcessFlowchartSidebar
                  schemas={props.designSpace.availableNodes}
                  rootSchema={props.designSpace.flowchartSchema}
                />
              </div>
            )}
          </div>
        </ProcessFlowchartContext.Provider>
      )}
    </ReactFlowWrapper>
  );
}

export default function ProcessFlowchartEditor(props: ProcessFlowchartEditorProps): JSX.Element {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const {width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver({
    ref: reactFlowWrapper,
  });
  return (
    <div ref={reactFlowWrapper} className={css(styles.flowchartWrapper)}>
      <ReactFlowProvider>
        <FlowchartCacheDataProvider>
          <InnerProcessFlowchartEditor {...props} reactFlowContainerWidth={reactFlowContainerWidth} />
        </FlowchartCacheDataProvider>
      </ReactFlowProvider>
    </div>
  );
}

const SIDEBAR_WIDTH = 178;

const styles = StyleSheet.create({
  sidebarNodeWrapper: {
    width: ProcessFlowchartNodeWidth.SIDEBAR,
    textAlign: 'center',
  },
  heading: {
    textAlign: 'center',
    fontSize: '14px',
    padding: '16px 0 8px 0',
    position: 'relative',
    zIndex: 5,
    color: GRAY_DARKER,
    background: GRAY_LIGHTER,
  },
  flowchartWrapper: {
    width: '100%',
    height: '100%',
    background: GRAY_LIGHTER,
  },
  flowchartInnerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  flowchartCanvas: {
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
  },
  flowchartCanvasNoSidebar: {
    flex: 1,
  },
  autoLayoutButton: {
    position: 'absolute',
    marginLeft: '16px',
    marginTop: '16px',
    zIndex: 5,
  },
});
